import { createGlobalState, type GlobalStateSetter } from "@/utilities/createGlobalState"

//--------------------------------------------------------------------------------
/** Common state for data grids */
export type DataGridState<T> = {
  rows: T[]
  /* The total number of rows on the server */
  totalRows: number
  /* Zero-based index of the row to start at */
  skip: number
  /* Number of rows to fetch */
  take: number
  /* Optional way to trigger refreshes by incrementing the number */
  refresh?: number
  /* If true then a change to .refresh should trigger .loadingQuietly instead of .loading */
  refreshQuietly?: boolean
  /* When the last refresh was performed */
  lastRefreshedAt?: number
  /* The id of the selected row */
  selectedId?: T[keyof T]
  /* Whether data is being loaded and the user should see this */
  loading?: boolean
  /* Whether data is being loaded but the user should not see this */
  loadingQuietly?: boolean
}

//--------------------------------------------------------------------------------
/** Hook to create a global state for a data grid */
export function createDataGridState<S extends DataGridState<any>>(
  key: string,
  initialState: S
) {
  return createGlobalState<S>(key, initialState)
}

//--------------------------------------------------------------------------------
/** Get the current page of the data grid (1-based) */
export function currentPage<T>(state: DataGridState<T>) {
  return Math.floor(state.skip / state.take) + 1
}

//--------------------------------------------------------------------------------
/** Get the total number of pages in the data grid */
export function totalPages<T>(state: DataGridState<T>) {
  return Math.ceil(state.totalRows / state.take)
}

//--------------------------------------------------------------------------------
/** 
 * Update the data grid state to point to the next data page. 
 * Return true if the state changed or false if not.
*/
export function nextPage<S extends DataGridState<any>>(
  setState: GlobalStateSetter<S>
) {
  let changed = false
  setState(prev => {
    const nextPageNum = currentPage(prev) + 1
    if (nextPageNum > totalPages(prev)) return prev  // Unchanged
    changed = true
    return {
      ...prev,
      skip: prev.skip + prev.take,
    } as S
  })
  return changed
}

//--------------------------------------------------------------------------------
/**
 * Update the data grid state to point to the previous data page. 
 * Return true if the state changed or false if not.
 */
export function previousPage<S extends DataGridState<any>>(
  setState: GlobalStateSetter<S>
) {
  let changed = false
  setState(prev => {
    const prevPageNum = currentPage(prev) - 1
    if (prevPageNum < 1) return prev  // Unchanged
    changed = true
    return {
      ...prev,
      skip: Math.max(0, prev.skip - prev.take),
    } as S
  })
  return changed
}

//--------------------------------------------------------------------------------
/**
 * Update the data grid state to point to the first data page. 
 * Return true if the state changed or false if not.
 */
export function firstPage<S extends DataGridState<any>>(
  setState: GlobalStateSetter<S>
) {
  let changed = false
  setState(prev => {
    if (currentPage(prev) === 1) return prev  // Unchanged
    changed = true
    return {
      ...prev,
      skip: 0,
    } as S
  })
  return changed
}

//--------------------------------------------------------------------------------
/**
 * Update the data grid state to point to the last data page. 
 * Return true if the state changed or false if not.
 */
export function lastPage<S extends DataGridState<any>>(
  setState: GlobalStateSetter<S>
) {
  let changed = false
  setState(prev => {
    if (currentPage(prev) === totalPages(prev)) return prev  // Unchanged
    changed = true
    return {
      ...prev,
      skip: (totalPages(prev) - 1) * prev.take,
    } as S
  })
  return changed
}

//--------------------------------------------------------------------------------
/** Indicate whether data is being loaded or not */
export function startLoading<S extends DataGridState<any>>(
  setState: GlobalStateSetter<S>,
  quietly?: boolean
) {
  setState(prev => ({
    ...prev,
    loading: !quietly,
    loadingQuietly: !!quietly,
    refresh: prev.refresh ?? 1,
    ...(!quietly ? { refreshQuietly: false } : {}),
  }))
}

//--------------------------------------------------------------------------------
/** Indicate that data loading is complete */
export function doneLoading<S extends DataGridState<any>>(
  setState: GlobalStateSetter<S>
) {
  setState(prev => ({
    ...prev,
    loading: false,
    loadingQuietly: false,
    refreshQuietly: false,
    lastRefreshedAt: Date.now(),
  }))
}

//--------------------------------------------------------------------------------
/** Increment the refresh index to trigger a data fetch */
export function incrementRefresh<S extends DataGridState<any>>(
  setState: GlobalStateSetter<S>,
  quietly?: boolean
) {
  setState(prev => ({
    ...prev,
    refresh: prev.refresh
      ? ((prev.refresh + 1) % 9999) + 1
      : 1,
    refreshQuietly: !!quietly,
  }))
}
